import React, { useEffect } from 'react';

import { Moment } from 'moment';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { useMediaQuery, useTheme } from '@material-ui/core';

import { updateHideCalendarDisabledEvents } from '../action/display-settings/update-hide-calendar-disabled-events';
import { criticalDatesForOrgIdSelector } from '../critical-dates/critical-dates-selectors';
import { userSelector } from '../reducers/current-user-selectors';
import {
  calendarEventFilters,
  hideCalendarDisabledEvents,
} from '../reducers/display-settings-selectors';

import { EventCalendarFilter } from './components/event-calendar-filter';
import { EventCalendarMonth } from './event-calendar-month';
import { EventCalendarPartialWeek } from './event-calendar-partial-week';

import { CalendarDisplayType } from './utils/calendar-display-type';
import { compareDates, SORT_ORDER } from '../util/sort-functions';

type InputProps = {
  orgId: number;
  calendarDisplay: ValueOf<typeof CalendarDisplayType>;
  calendarStart: Moment;
  setCalendarStart: (start: Moment) => void;
  className?: string;
};

interface StateProps {
  currentUser: AuthenticatedUser;
  eventFilters: Array<SelectOption<string>>;
  hideDisabledEvents: boolean;
  criticalDatesByDate: { [date: string]: Array<CriticalDate> };
}
const mapStateToProps = (state: any, { orgId }: InputProps): StateProps => {
  const currentUser = userSelector(state);

  return {
    currentUser,
    hideDisabledEvents: hideCalendarDisabledEvents(state, currentUser.username),
    eventFilters: calendarEventFilters(state, currentUser.username),
    criticalDatesByDate: criticalDatesForOrgIdSelector(state, orgId),
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  actions: {
    updateHideCalendarDisabledEvents: (hideDisabledEvents: boolean, user: AuthenticatedUser) =>
      dispatch(updateHideCalendarDisabledEvents(hideDisabledEvents, user.username)),
  },
});

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const EventCalendarBody = connect(
  mapStateToProps,
  mapDispatchToProps,
)((props: Props) => {
  const {
    orgId,
    currentUser,
    calendarStart,
    setCalendarStart,
    className,
    calendarDisplay,
    criticalDatesByDate,
    actions,
  } = props;

  const theme = useTheme();
  const onMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // For mobile users, we always want to default to hiding disabled events.
  // Whenever we change onMobile, we will update this setting to the user selection (desktop) or hiding disabled (mobile)
  useEffect(() => {
    if (onMobile) {
      actions.updateHideCalendarDisabledEvents(true, currentUser);
    }
  }, [onMobile, currentUser, actions]);

  // Returns all communicationEnabled dates first, then all disabled
  // Each group sorted by All Day, then start time, and finally title
  Object.values(criticalDatesByDate || {}).forEach((criticalDates) => {
    criticalDates.sort((a, b) => {
      let sortVal = Number(b.communicationEnabled) - Number(a.communicationEnabled);
      if (!sortVal) {
        sortVal = Number(b.allDay) - Number(a.allDay);
      }
      if (!sortVal) {
        sortVal = compareDates(a.startAt, b.startAt, SORT_ORDER.ASC, 'minute');
      }
      if (!sortVal) {
        sortVal = a.title ? a.title.localeCompare(b.title) : 0;
      }
      return sortVal;
    });
  });

  return (
    <div className={className || ''}>
      <EventCalendarFilter
        calendarStart={calendarStart}
        setCalendarStart={setCalendarStart}
        calendarDisplay={calendarDisplay}
      />
      {calendarDisplay === CalendarDisplayType.PARTIAL_WEEK ? (
        <EventCalendarPartialWeek
          orgId={orgId}
          criticalDatesByDate={criticalDatesByDate}
          calendarStart={calendarStart}
          onMobile={onMobile}
        />
      ) : (
        <EventCalendarMonth
          orgId={orgId}
          criticalDatesByDate={criticalDatesByDate}
          calendarStart={calendarStart}
        />
      )}
    </div>
  );
});
